import { useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface Props {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  autoFocus?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  error?: string | null;
  helpText?: string;
  id?: string;
}

/** Single-source password field with a show/hide eye toggle. Used on Login,
 *  AcceptInvite, ForgotPassword and ResetPassword so keyboard/screen-reader
 *  behavior is identical across the auth flow. */
export function PasswordInput({
  value,
  onChange,
  label = "Password",
  placeholder,
  required = true,
  minLength,
  autoFocus,
  autoComplete = "current-password",
  disabled,
  error,
  helpText,
  id: idProp,
}: Props) {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const id = idProp || generatedId;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          className={`input pr-10 ${error ? "!ring-rose-400 !border-rose-400" : ""}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          minLength={minLength}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : helpText ? helpId : undefined}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand rounded p-1"
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          tabIndex={0}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-rose-700">{error}</p>
      ) : helpText ? (
        <p id={helpId} className="mt-1 text-xs text-slate-500">{helpText}</p>
      ) : null}
    </div>
  );
}
